/**
 * Gamification & Achievements Page
 * Points, badges, leaderboards, and safety culture engagement
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  Trophy, Medal, Star, Flame, Crown, Zap, Target, Award,
  TrendingUp, Calendar, Users, Heart, FileWarning, GraduationCap,
  MessageSquare, RefreshCw, ChevronUp, Sparkles, Gift
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Badge rarity colors
const RARITY_COLORS = {
  common: 'bg-gray-500',
  uncommon: 'bg-green-500',
  rare: 'bg-blue-500',
  epic: 'bg-purple-500',
  legendary: 'bg-amber-500'
};

const RARITY_BORDERS = {
  common: 'border-gray-500',
  uncommon: 'border-green-500',
  rare: 'border-blue-500',
  epic: 'border-purple-500',
  legendary: 'border-amber-500 animate-pulse'
};

// Icon mapping
const BADGE_ICONS = {
  FileWarning: FileWarning,
  Eye: Target,
  Trophy: Trophy,
  Sparkles: Sparkles,
  GraduationCap: GraduationCap,
  BookOpen: GraduationCap,
  Award: Award,
  Calendar: Calendar,
  Flame: Flame,
  Crown: Crown,
  MessageSquare: MessageSquare,
  Users: Users,
  Medal: Medal,
  Heart: Heart,
  Zap: Zap
};

export default function GamificationPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardTimeframe, setLeaderboardTimeframe] = useState('all_time');
  const [loading, setLoading] = useState(true);
  const [pointsHistory, setPointsHistory] = useState([]);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDashboard();
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [leaderboardTimeframe]);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gamification/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gamification/leaderboard?timeframe=${leaderboardTimeframe}&limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data.leaderboard || []);
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };

  const fetchPointsHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gamification/points/history?limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPointsHistory(data.transactions || []);
      }
    } catch (error) {
      console.error('Failed to fetch points history:', error);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  const profile = dashboard?.profile || {};
  const levelProgress = profile.total_points 
    ? ((profile.total_points % 1000) / 1000) * 100 
    : 0;

  return (
    <div className="space-y-6 p-6" data-testid="gamification-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Trophy className="h-6 w-6 text-amber-500" />
            {t('gamification.title')}
          </h1>
          <p className="text-gray-400 mt-1">
            {t('gamification.subtitle')}
          </p>
        </div>
        <Button variant="outline" onClick={fetchDashboard}>
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Profile Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Level Card */}
        <Card className="bg-gradient-to-br from-amber-900/40 to-amber-950/60 border-amber-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-amber-400 text-sm">Level {profile.level || 1}</p>
                <p className="text-2xl font-bold text-white">{profile.level_name || 'Rookie'}</p>
              </div>
              <Crown className="h-10 w-10 text-amber-500" />
            </div>
            <Progress value={levelProgress} className="h-2 bg-amber-950" />
            <p className="text-xs text-amber-400/70 mt-1">
              {dashboard?.next_level?.points_needed || 0} points to next level
            </p>
          </CardContent>
        </Card>

        {/* Points Card */}
        <Card className="bg-gradient-to-br from-sky-900/40 to-sky-950/60 border-sky-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sky-400 text-sm">Total Points</p>
                <p className="text-3xl font-bold text-white">{profile.total_points?.toLocaleString() || 0}</p>
              </div>
              <Star className="h-10 w-10 text-sky-500" />
            </div>
            <p className="text-xs text-sky-400/70 mt-2">
              +{profile.weekly_points || 0} this week
            </p>
          </CardContent>
        </Card>

        {/* Streak Card */}
        <Card className="bg-gradient-to-br from-orange-900/40 to-orange-950/60 border-orange-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-400 text-sm">Current Streak</p>
                <p className="text-3xl font-bold text-white">{profile.current_streak || 0} days</p>
              </div>
              <Flame className="h-10 w-10 text-orange-500" />
            </div>
            <p className="text-xs text-orange-400/70 mt-2">
              Best: {profile.longest_streak || 0} days
            </p>
          </CardContent>
        </Card>

        {/* Badges Card */}
        <Card className="bg-gradient-to-br from-purple-900/40 to-purple-950/60 border-purple-800">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-400 text-sm">Badges Earned</p>
                <p className="text-3xl font-bold text-white">{dashboard?.earned_badges?.length || 0}</p>
              </div>
              <Medal className="h-10 w-10 text-purple-500" />
            </div>
            <p className="text-xs text-purple-400/70 mt-2">
              #{dashboard?.leaderboard_position || '--'} in organization
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Star className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="badges" data-testid="tab-badges">
            <Medal className="h-4 w-4 mr-2" />
            Badges
          </TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
            <Trophy className="h-4 w-4 mr-2" />
            Leaderboard
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history" onClick={fetchPointsHistory}>
            <Calendar className="h-4 w-4 mr-2" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Points */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  {dashboard?.recent_points?.length > 0 ? (
                    <div className="space-y-3">
                      {dashboard.recent_points.map((tx, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center">
                              <Zap className="h-4 w-4 text-sky-400" />
                            </div>
                            <div>
                              <p className="text-sm text-white">{tx.description}</p>
                              <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                            </div>
                          </div>
                          <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                            +{tx.points}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Gift className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Start earning points by reporting hazards!</p>
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Next Badges */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-purple-500" />
                  Next Achievements
                </CardTitle>
                <CardDescription>Badges you're close to earning</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {dashboard?.available_badges?.slice(0, 4).map((badge, i) => {
                    const IconComponent = BADGE_ICONS[badge.icon] || Medal;
                    return (
                      <div key={i} className="p-3 bg-slate-800/50 rounded-lg">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`w-10 h-10 rounded-lg ${RARITY_COLORS[badge.rarity]} flex items-center justify-center`}>
                            <IconComponent className="h-5 w-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-medium">{badge.name}</p>
                            <p className="text-xs text-gray-500">{badge.description}</p>
                          </div>
                          <Badge variant="outline" className={RARITY_BORDERS[badge.rarity]}>
                            {badge.rarity}
                          </Badge>
                        </div>
                        <Progress value={badge.progress || 0} className="h-2" />
                        <p className="text-xs text-gray-500 mt-1">{Math.round(badge.progress || 0)}% complete</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Badges Tab */}
        <TabsContent value="badges" className="space-y-6">
          {/* Earned Badges */}
          <Card>
            <CardHeader>
              <CardTitle>Earned Badges ({dashboard?.earned_badges?.length || 0})</CardTitle>
              <CardDescription>Your achievements collection</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.earned_badges?.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {dashboard.earned_badges.map((badge, i) => {
                    const IconComponent = BADGE_ICONS[badge.icon] || Medal;
                    return (
                      <div
                        key={i}
                        className={`p-4 rounded-xl border-2 ${RARITY_BORDERS[badge.rarity]} bg-slate-800/50 text-center hover:scale-105 transition-transform cursor-pointer`}
                        title={badge.description}
                      >
                        <div className={`w-16 h-16 rounded-full ${RARITY_COLORS[badge.rarity]} flex items-center justify-center mx-auto mb-2`}>
                          <IconComponent className="h-8 w-8 text-white" />
                        </div>
                        <p className="text-white font-medium text-sm">{badge.name}</p>
                        <p className="text-xs text-gray-500 mt-1">{formatDate(badge.earned_at)}</p>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Medal className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">No badges earned yet</p>
                  <p className="text-sm">Complete safety activities to earn your first badge!</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Available Badges */}
          <Card>
            <CardHeader>
              <CardTitle>Available Badges</CardTitle>
              <CardDescription>Badges you can still earn</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {dashboard?.available_badges?.map((badge, i) => {
                  const IconComponent = BADGE_ICONS[badge.icon] || Medal;
                  return (
                    <div key={i} className="p-4 bg-slate-800/30 rounded-lg border border-slate-700">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-12 h-12 rounded-lg bg-slate-700 flex items-center justify-center opacity-50`}>
                          <IconComponent className="h-6 w-6 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{badge.name}</p>
                          <Badge variant="outline" className="text-xs">{badge.rarity}</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 mb-2">{badge.description}</p>
                      <Progress value={badge.progress || 0} className="h-2" />
                      <div className="flex justify-between mt-1">
                        <p className="text-xs text-gray-500">{Math.round(badge.progress || 0)}%</p>
                        <p className="text-xs text-amber-500">+{badge.points_reward} pts</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Organization Leaderboard</CardTitle>
                  <CardDescription>Top safety contributors</CardDescription>
                </div>
                <div className="flex gap-2">
                  {['all_time', 'monthly', 'weekly'].map((tf) => (
                    <Button
                      key={tf}
                      variant={leaderboardTimeframe === tf ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setLeaderboardTimeframe(tf)}
                    >
                      {tf === 'all_time' ? 'All Time' : tf === 'monthly' ? 'Monthly' : 'Weekly'}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leaderboard.map((entry, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-4 p-4 rounded-lg ${
                      entry.rank <= 3 ? 'bg-gradient-to-r from-amber-900/30 to-transparent border border-amber-800/50' : 'bg-slate-800/50'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                      entry.rank === 1 ? 'bg-amber-500 text-black' :
                      entry.rank === 2 ? 'bg-gray-300 text-black' :
                      entry.rank === 3 ? 'bg-amber-700 text-white' :
                      'bg-slate-700 text-white'
                    }`}>
                      {entry.rank <= 3 ? (
                        entry.rank === 1 ? <Crown className="h-5 w-5" /> :
                        entry.rank === 2 ? <Medal className="h-5 w-5" /> :
                        <Award className="h-5 w-5" />
                      ) : entry.rank}
                    </div>
                    
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-sky-600 text-white">
                        {getInitials(entry.user_name)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1">
                      <p className="text-white font-medium">{entry.user_name}</p>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>Level {entry.level} {entry.level_name}</span>
                        <span>•</span>
                        <span>{entry.badges_count} badges</span>
                        {entry.current_streak > 0 && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Flame className="h-3 w-3 text-orange-500" />
                              {entry.current_streak}d
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-xl font-bold text-white">{entry.total_points.toLocaleString()}</p>
                      <p className="text-xs text-gray-500">points</p>
                    </div>
                  </div>
                ))}
                
                {leaderboard.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No leaderboard data yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Points History</CardTitle>
              <CardDescription>All your earned points</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {pointsHistory.length > 0 ? (
                  <div className="space-y-2">
                    {pointsHistory.map((tx, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg hover:bg-slate-800/50 transition-colors">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-sky-500/20 flex items-center justify-center">
                            <Zap className="h-4 w-4 text-sky-400" />
                          </div>
                          <div>
                            <p className="text-white">{tx.description}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(tx.created_at).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <Badge className="bg-green-500/20 text-green-400 border-green-500/50">
                          +{tx.points}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No points history yet</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
